/**
 * DeployConnector — build at a git ref and push to remote deployment targets.
 *
 * Supported providers: mock, fly, vercel, docker-push, rsync.
 * Only `mock` is fully implemented.
 */

import { execFile } from "node:child_process";
import { promisify } from "node:util";
import type {
  ConnectContext,
  ConnectorDeclaration,
  ConnectorHandle,
  OperationDescriptor,
  RuntimeSkillDescriptor,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";

const execFileAsync = promisify(execFile);

/**
 * Deadline for the repo-root probe in `connect()`. `rev-parse` is a metadata
 * read on a healthy repo; anything slower means the filesystem under `cwd`
 * is not answering, and `connect()` runs on the session-build handler.
 */
const GIT_REV_PARSE_TIMEOUT_MS = 15_000;

interface DeployState {
  provider: string;
  defaults: Record<string, string>;
  deployments: Map<string, DeploymentRecord>;
  repoRoot: string | null;
  worktreeDir: string;
}

interface DeploymentRecord {
  id: string;
  url: string;
  ref?: string;
  buildCommand: string;
  buildDir: string;
  deployedAt: string;
  status: "deploying" | "live" | "failed" | "removed";
}

/**
 * Connector for building and deploying to remote targets.
 * Supports push, status, list, teardown, and logs operations. Currently implements the `mock` provider; fly, vercel, docker-push, and rsync are planned.
 * @docLink packages/factory-assets/concepts#deploy-connector
 */
export class DeployConnector extends AbstractConnector {
  readonly name = "deploy";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
      describeSkill: (handle, declaration) => this._describeSkill(handle, declaration),
    };
  }

  async connect(declaration: ConnectorDeclaration, _ctx: ConnectContext): Promise<ConnectorHandle> {
    const provider = String(declaration.options?.provider ?? "mock");
    this.log.info("connect", { id: declaration.id, provider });
    const defaults: Record<string, string> = {};
    for (const [k, v] of Object.entries(declaration.options ?? {})) {
      if (k !== "provider" && typeof v === "string") defaults[k] = v;
    }
    // Explicit cwd: the previous call inherited the process working directory
    // implicitly, so wherever `skaile serve` happened to be started — possibly
    // inside a FUSE mount — silently became git's search root. `repo_root`
    // makes it configurable; the default is the same directory as before, but
    // now stated rather than inherited.
    const repoDir = String(declaration.options?.repo_root ?? process.cwd());
    let repoRoot: string | null = null;
    try {
      // execFile (not execSync) + explicit timeout + SIGKILL: `connect()` runs
      // on the session-build handler with the WebSocket live, so a git call
      // that never returns used to take the whole runner with it. SIGTERM is
      // not delivered to a `D`-state process on a dead FUSE connection.
      const { stdout } = await execFileAsync("git", ["rev-parse", "--show-toplevel"], {
        cwd: repoDir,
        encoding: "utf-8",
        timeout: GIT_REV_PARSE_TIMEOUT_MS,
        killSignal: "SIGKILL",
      });
      repoRoot = stdout.trim();
    } catch {}
    return this.makeHandle(declaration, {
      provider,
      defaults,
      deployments: new Map(),
      repoRoot,
      worktreeDir: repoDir,
    } as DeployState);
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    handle.status = "disconnected";
  }

  override async onSessionClose(_handle: ConnectorHandle): Promise<string | undefined> {
    return undefined;
  }

  override async onHibernate(_handle: ConnectorHandle): Promise<string | undefined> {
    return undefined;
  }

  // ── Tool face methods (private, wired in constructor) ─────────────────────

  private _describeOperations(_handle: ConnectorHandle): OperationDescriptor[] {
    return [
      {
        name: "push",
        description: "Build and deploy.",
        accessLevel: "write",
        args: [
          { name: "build_command", type: "string", required: true, description: "Build command" },
          {
            name: "build_dir",
            type: "string",
            required: true,
            description: "Build output directory",
          },
          { name: "ref", type: "string", required: false, description: "Git ref" },
          { name: "label", type: "string", required: false, description: "Deployment label" },
          {
            name: "target_overrides",
            type: "json",
            required: false,
            description: "Provider overrides",
          },
        ],
      },
      {
        name: "status",
        description: "Get deployment status.",
        accessLevel: "read",
        args: [
          {
            name: "deployment",
            type: "string",
            required: false,
            description: "Deployment ID (omit for all)",
          },
        ],
      },
      { name: "list", description: "List all deployments.", accessLevel: "read", args: [] },
      {
        name: "teardown",
        description: "Remove a deployment.",
        accessLevel: "write",
        args: [
          { name: "deployment", type: "string", required: true, description: "Deployment ID" },
        ],
      },
      {
        name: "logs",
        description: "Fetch remote logs.",
        accessLevel: "read",
        args: [
          { name: "deployment", type: "string", required: true, description: "Deployment ID" },
          {
            name: "lines",
            type: "number",
            required: false,
            description: "Number of lines (default: 100)",
          },
        ],
      },
    ];
  }

  private _describeSkill(
    handle: ConnectorHandle,
    declaration: ConnectorDeclaration,
  ): RuntimeSkillDescriptor {
    const state = handle.state as DeployState;
    return {
      id: declaration.id,
      name: `Deploy — ${state.provider}`,
      description: `Build and deploy to ${state.provider}.`,
      category: "service",
      access: handle.access,
      toolNames: ["push", "status", "list", "teardown", "logs"],
    };
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const state = handle.state as DeployState;
    switch (operation) {
      case "push":
        return this._opPush(handle, state, args);
      case "status":
        return this._opStatus(state, args);
      case "list":
        return this._opList(state);
      case "teardown":
        return this._opTeardown(handle, state, args);
      case "logs":
        return this._opLogs(state, args);
      default:
        throw new Error(`DeployConnector has no operation: ${operation}`);
    }
  }

  private async _opPush(
    handle: ConnectorHandle,
    state: DeployState,
    args: Record<string, unknown>,
  ): Promise<string> {
    if (handle.access === "read-only") throw new Error("push requires read-write access");
    const buildCommand = String(args.build_command ?? "");
    const buildDir = String(args.build_dir ?? "");
    if (!buildCommand) throw new Error("push: build_command required");
    if (!buildDir) throw new Error("push: build_dir required");
    const deploymentId = args.label ? String(args.label) : `deploy-${Date.now()}`;
    this.log.info("build start", {
      deploymentId,
      buildCommand,
      buildDir,
      provider: state.provider,
    });
    try {
      let result: string;
      switch (state.provider) {
        case "mock":
          result = this._mockPush(state, {
            deploymentId,
            buildCommand,
            buildDir,
            ref: args.ref ? String(args.ref) : undefined,
          });
          break;
        default:
          throw new Error(`Provider "${state.provider}" is not yet implemented`);
      }
      this.log.info("build success", { deploymentId });
      return result;
    } catch (err) {
      this.log.error("build failed", err, { deploymentId });
      throw err;
    }
  }

  private _mockPush(
    state: DeployState,
    opts: { deploymentId: string; buildCommand: string; buildDir: string; ref?: string },
  ): string {
    const record: DeploymentRecord = {
      id: opts.deploymentId,
      url: `https://mock.deploy.example.com/${opts.deploymentId}`,
      ref: opts.ref,
      buildCommand: opts.buildCommand,
      buildDir: opts.buildDir,
      deployedAt: new Date().toISOString(),
      status: "live",
    };
    state.deployments.set(opts.deploymentId, record);
    return JSON.stringify({ deployment_id: record.id, url: record.url, ref: record.ref ?? null });
  }

  private _opStatus(state: DeployState, args: Record<string, unknown>): string {
    const id = args.deployment ? String(args.deployment) : undefined;
    if (id) {
      const r = state.deployments.get(id);
      if (!r) throw new Error(`Deployment not found: ${id}`);
      return JSON.stringify(this._ser(r));
    }
    return JSON.stringify([...state.deployments.values()].map((r) => this._ser(r)));
  }

  private _opList(state: DeployState): string {
    return JSON.stringify([...state.deployments.values()].map((r) => this._ser(r)));
  }

  private _opTeardown(
    handle: ConnectorHandle,
    state: DeployState,
    args: Record<string, unknown>,
  ): string {
    if (handle.access === "read-only") throw new Error("teardown requires read-write access");
    const id = args.deployment ? String(args.deployment) : undefined;
    if (!id) throw new Error("teardown: deployment required");
    if (state.provider === "mock") {
      if (!state.deployments.delete(id)) throw new Error(`Not found: ${id}`);
      return JSON.stringify({ removed: true });
    }
    throw new Error(`Provider "${state.provider}" does not support teardown yet`);
  }

  private _opLogs(state: DeployState, args: Record<string, unknown>): string {
    const id = args.deployment ? String(args.deployment) : undefined;
    if (!id) throw new Error("logs: deployment required");
    if (state.provider === "mock")
      return JSON.stringify({ deployment: id, logs: "No logs available for mock provider." });
    throw new Error(`Provider "${state.provider}" does not support logs yet`);
  }

  private _ser(r: DeploymentRecord): Record<string, unknown> {
    return {
      id: r.id,
      url: r.url,
      ref: r.ref ?? null,
      status: r.status,
      deployedAt: r.deployedAt,
      buildCommand: r.buildCommand,
      buildDir: r.buildDir,
    };
  }
}

/**
 * Creates a new DeployConnector instance.
 * @returns Configured DeployConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#deploy-connector-factory
 */
export function createConnector(): DeployConnector {
  return new DeployConnector();
}
